import { create } from 'zustand'
import * as Sentry from '@sentry/react'
import { getSharedSession, refreshSession, logoutSession } from '../services/authApi'
import { AUTH_LABELS } from '../constants/labels'

const SESSION_AUTH_IDENTIFIER_KEY = '__authIdentifier'

const getSessionDataFromAuthPayload = (payload) => {
  if (!payload || typeof payload !== 'object') return null

  if ('SE_IDUSUARIO' in payload || 'SE_CONTRATO' in payload || 'SE_INFO' in payload) {
    return payload
  }

  return null
}

const getTokensFromAuthPayload = (payload) => {
  if (!payload || typeof payload !== 'object') return null

  if (payload.SE_INFO?.keycloak_token) {
    return payload.SE_INFO.keycloak_token
  }

  if (payload.tokens) {
    return payload.tokens
  }

  return payload
}

const getSessionAuthIdentifier = (sessionData) =>
  sessionData?.SE_TOKEN ?? sessionData?.[SESSION_AUTH_IDENTIFIER_KEY] ?? null

const attachSessionAuthIdentifier = (sessionData, authIdentifier) => {
  if (!sessionData) return null

  const resolvedAuthIdentifier = authIdentifier ?? getSessionAuthIdentifier(sessionData)
  if (!resolvedAuthIdentifier) return sessionData

  return {
    ...sessionData,
    [SESSION_AUTH_IDENTIFIER_KEY]: resolvedAuthIdentifier,
  }
}

// BroadcastChannel para sincronizar sesión entre tabs.
// sessionStorage no se comparte entre tabs, pero las cookies HttpOnly sí (nivel browser),
// por lo que una tab nueva puede obtener session_data de una tab existente y luego
// llamar a refreshSession() con la cookie compartida para obtener sus propios tokens.
const CHANNEL_NAME = 'auth_session_sync'
const REFRESH_BUFFER_MS = 120000
const SESSION_DATA_KEY = 'session_data'
const SESSION_SNAPSHOT_KEY = 'session_snapshot'
const BROADCAST_TIMEOUT_MS = 1500

let refreshPromise = null

const parseJwtPayload = (token) => {
  if (!token) return null

  const parts = token.split('.')
  if (parts.length < 2) return null

  try {
    const normalized = parts[1].replaceAll('-', '+').replaceAll('_', '/')
    const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=')
    return JSON.parse(globalThis.atob(padded))
  } catch {
    return null
  }
}

const getTokenRemainingMs = (tokens, tokenTimestamp) => {
  if (!tokens) return 0

  const payload = parseJwtPayload(tokens.access_token)
  const expMs = Number(payload?.exp || 0) * 1000

  if (expMs > 0) {
    return Math.max(0, expMs - Date.now())
  }

  if (!tokenTimestamp) return 0

  return Math.max(0, tokenTimestamp + Number(tokens.expires_in || 0) * 1000 - Date.now())
}

const isAuthError = (error) => {
  const status = error?.response?.status
  return status === 401 || status === 403
}

const buildSessionSnapshot = (sessionData) => {
  if (!sessionData) return null

  return {
    SE_IDUSUARIO: sessionData.SE_IDUSUARIO ?? null,
    SE_CONTRATO: sessionData.SE_CONTRATO ?? null,
    SE_INFO: {
      email: sessionData.SE_INFO?.email ?? null,
      cuit: sessionData.SE_INFO?.cuit ?? null,
      razon_social: sessionData.SE_INFO?.razon_social ?? null,
    },
  }
}

const persistSessionSnapshot = (sessionData) => {
  const snapshot = buildSessionSnapshot(sessionData)
  if (!snapshot) return
  localStorage.setItem(SESSION_SNAPSHOT_KEY, JSON.stringify(snapshot))
}

const readSessionSnapshot = () => {
  const snapshotStr = localStorage.getItem(SESSION_SNAPSHOT_KEY)
  if (!snapshotStr) return null

  try {
    return JSON.parse(snapshotStr)
  } catch {
    localStorage.removeItem(SESSION_SNAPSHOT_KEY)
    return null
  }
}

const clearSessionStorage = () => {
  sessionStorage.removeItem(SESSION_DATA_KEY)
  localStorage.removeItem(SESSION_SNAPSHOT_KEY)
}

const buildUserState = (sessionData) => ({
  id: sessionData.SE_IDUSUARIO,
  contrato: sessionData.SE_CONTRATO,
  email: sessionData.SE_INFO?.email,
  cuit: sessionData.SE_INFO?.cuit,
  razonSocial: sessionData.SE_INFO?.razon_social,
})

const requestSessionFromOtherTab = () => {
  if (typeof BroadcastChannel === 'undefined') return Promise.resolve(null)

  return new Promise((resolve) => {
    const channel = new BroadcastChannel(CHANNEL_NAME)

    const timeout = setTimeout(() => {
      channel.close()
      resolve(null)
    }, BROADCAST_TIMEOUT_MS)

    channel.onmessage = ({ data }) => {
      if (data.type === 'SESSION_RESPONSE') {
        clearTimeout(timeout)
        channel.close()
        resolve(data.sessionData)
      }
    }

    channel.postMessage({ type: 'REQUEST_SESSION' })
  })
}

const getAuthErrorMessage = (error) => {
  if (error.response?.status === 404) {
    return AUTH_LABELS.INVALID_LINK_MESSAGE
  }

  return error.response?.data?.message || error.message || AUTH_LABELS.DEFAULT_AUTH_ERROR
}

const useAuthStore = create((set, get) => ({
  isAuthenticated: false,
  isLoading: true,
  isRefreshing: false,
  user: null,
  tokens: null,
  tokenTimestamp: null,
  sessionData: null,
  error: null,

  _setSentryUser: (userData) => {
    if (userData) {
      Sentry.setUser({
        id: userData.SE_IDUSUARIO?.toString(),
        email: userData.SE_INFO?.email,
        username: userData.SE_INFO?.email,
        contrato: userData.SE_CONTRATO?.toString(),
      })
    } else {
      Sentry.setUser(null)
    }
  },

  loginWithUUID: async (uuid) => {
    try {
      set({ isLoading: true, error: null })

      const authPayload = await getSharedSession(uuid)
      const rawSessionData = getSessionDataFromAuthPayload(authPayload)
      const tokens = getTokensFromAuthPayload(authPayload)
      const sessionData = attachSessionAuthIdentifier(rawSessionData, uuid)

      if (!sessionData || !tokens?.access_token) {
        throw new Error('Respuesta de sesión inválida')
      }

      const tokenTimestamp = Date.now()

      sessionStorage.setItem(SESSION_DATA_KEY, JSON.stringify(sessionData))
      persistSessionSnapshot(sessionData)

      get()._setSentryUser(sessionData)

      set({
        isAuthenticated: true,
        isLoading: false,
        user: buildUserState(sessionData),
        tokens,
        tokenTimestamp,
        sessionData,
        error: null,
      })

      Sentry.addBreadcrumb({
        category: 'auth',
        message: 'Login exitoso con UUID',
        level: 'info',
      })

      const remainingMs = getTokenRemainingMs(tokens, tokenTimestamp)
      if (remainingMs <= REFRESH_BUFFER_MS) {
        try {
          await get().refreshToken()
        } catch {
          // refreshToken maneja logout únicamente para errores de autenticación
        }
      }

      return { success: true }
    } catch (error) {
      const errorMessage = getAuthErrorMessage(error)

      Sentry.captureException(error, {
        tags: { feature: 'auth', action: 'login-uuid' },
      })

      set({
        isAuthenticated: false,
        isLoading: false,
        user: null,
        tokens: null,
        tokenTimestamp: null,
        sessionData: null,
        error: errorMessage,
      })

      return { success: false, error: errorMessage }
    }
  },

  refreshToken: async () => {
    if (refreshPromise) return refreshPromise

    set({ isRefreshing: true })

    refreshPromise = (async () => {
      try {
        const state = get()
        const authPayload = await refreshSession()
        const newTokens = getTokensFromAuthPayload(authPayload)
        const refreshedSessionData = attachSessionAuthIdentifier(
          getSessionDataFromAuthPayload(authPayload) ?? state.sessionData
        )

        if (!newTokens?.access_token) {
          throw new Error('Respuesta de refresh inválida')
        }

        if (refreshedSessionData) {
          sessionStorage.setItem(SESSION_DATA_KEY, JSON.stringify(refreshedSessionData))
          persistSessionSnapshot(refreshedSessionData)
          state._setSentryUser(refreshedSessionData)
        }

        set({
          isAuthenticated: true,
          user: refreshedSessionData ? buildUserState(refreshedSessionData) : state.user,
          tokens: newTokens,
          tokenTimestamp: Date.now(),
          sessionData: refreshedSessionData ?? state.sessionData,
          error: null,
        })

        return newTokens
      } catch (error) {
        Sentry.captureException(error, {
          tags: { feature: 'auth', action: 'refresh-token' },
        })

        if (isAuthError(error)) {
          get().logout()
        }

        throw error
      } finally {
        refreshPromise = null
        set({ isRefreshing: false })
      }
    })()

    return refreshPromise
  },

  refreshSessionData: async () => {
    const state = get()
    const sessionIdentity = state.sessionData

    if (!sessionIdentity) return null

    try {
      set({ isRefreshing: true })

      const authIdentifier = getSessionAuthIdentifier(sessionIdentity)
      const authPayload = authIdentifier ? await getSharedSession(authIdentifier) : await refreshSession()
      const refreshedSessionData = attachSessionAuthIdentifier(
        getSessionDataFromAuthPayload(authPayload) ?? sessionIdentity,
        authIdentifier
      )
      const newTokens = getTokensFromAuthPayload(authPayload)

      if (!newTokens?.access_token) {
        throw new Error('Respuesta de refresh inválida')
      }

      sessionStorage.setItem(SESSION_DATA_KEY, JSON.stringify(refreshedSessionData))
      persistSessionSnapshot(refreshedSessionData)
      state._setSentryUser(refreshedSessionData)

      set({
        isAuthenticated: true,
        user: buildUserState(refreshedSessionData),
        tokens: newTokens,
        tokenTimestamp: Date.now(),
        sessionData: refreshedSessionData,
        error: null,
      })

      return refreshedSessionData
    } catch (error) {
      Sentry.captureException(error, {
        tags: { feature: 'auth', action: 'refresh-session-data' },
      })

      if (isAuthError(error)) {
        get().logout()
      }

      throw error
    } finally {
      set({ isRefreshing: false })
    }
  },

  logout: () => {
    const state = get()
    clearSessionStorage()
    state._setSentryUser(null)

    set({
      isAuthenticated: false,
      isLoading: false,
      user: null,
      tokens: null,
      tokenTimestamp: null,
      sessionData: null,
      error: null,
    })

    Sentry.addBreadcrumb({
      category: 'auth',
      message: 'Logout exitoso',
      level: 'info',
    })

    // Notificar a otras tabs para que también cierren sesión localmente
    if (typeof BroadcastChannel !== 'undefined') {
      const channel = new BroadcastChannel(CHANNEL_NAME)
      channel.postMessage({ type: 'LOGOUT' })
      channel.close()
    }

    logoutSession()
  },

  getAccessToken: () => {
    return get().tokens?.access_token || null
  },

  isTokenExpiringSoon: () => {
    const { tokens, tokenTimestamp } = get()

    if (!tokens || !tokenTimestamp) return true

    return getTokenRemainingMs(tokens, tokenTimestamp) < REFRESH_BUFFER_MS
  },

  restoreSession: async () => {
    const state = get()
    let sessionDataStr = sessionStorage.getItem(SESSION_DATA_KEY)
    let sessionData = null
    let snapshot = null

    if (sessionDataStr) {
      try {
        sessionData = JSON.parse(sessionDataStr)
      } catch {
        sessionStorage.removeItem(SESSION_DATA_KEY)
      }
    }

    if (!sessionData) {
      snapshot = readSessionSnapshot()
    }

    // Si no hay nada local para reconstruir usuario, pedirlo a otra tab.
    if (!sessionData && !snapshot) {
      const sessionDataFromTab = await requestSessionFromOtherTab()
      if (sessionDataFromTab) {
        sessionData = sessionDataFromTab
        sessionStorage.setItem(SESSION_DATA_KEY, JSON.stringify(sessionDataFromTab))
        persistSessionSnapshot(sessionDataFromTab)
      }
    }

    const sessionIdentity = sessionData || snapshot

    if (!sessionIdentity) {
      set({ isLoading: false })
      return
    }

    try {
      const authIdentifier = getSessionAuthIdentifier(sessionIdentity)
      let restoredSessionData = sessionData || sessionIdentity
      let newTokens = null

      if (authIdentifier) {
        const authPayload = await getSharedSession(authIdentifier)
        restoredSessionData = attachSessionAuthIdentifier(
          getSessionDataFromAuthPayload(authPayload) ?? restoredSessionData,
          authIdentifier
        )
        newTokens = getTokensFromAuthPayload(authPayload)
      } else {
        // Las cookies HttpOnly son compartidas entre tabs por el browser,
        // por lo que refreshSession() funciona desde cualquier tab.
        const authPayload = await refreshSession()
        newTokens = getTokensFromAuthPayload(authPayload)
        restoredSessionData = attachSessionAuthIdentifier(
          getSessionDataFromAuthPayload(authPayload) ?? restoredSessionData
        )
      }

      if (!newTokens?.access_token) {
        throw new Error('Respuesta de refresh inválida')
      }

      sessionStorage.setItem(SESSION_DATA_KEY, JSON.stringify(restoredSessionData))
      state._setSentryUser(restoredSessionData)

      set({
        isAuthenticated: true,
        isLoading: false,
        user: buildUserState(restoredSessionData),
        tokens: newTokens,
        tokenTimestamp: Date.now(),
        sessionData: restoredSessionData,
        error: null,
      })

      persistSessionSnapshot(restoredSessionData)
    } catch (error) {
      Sentry.captureException(error, {
        tags: { feature: 'auth', action: 'restore-session' },
      })

      // Acción 1: solo limpiar sesión en errores de auth, no en errores de red transitorios
      if (isAuthError(error)) {
        clearSessionStorage()
      }

      set({ isLoading: false })
    }
  },
}))

// Listener persistente para responder a solicitudes de sesión de otras tabs
// y propagar logouts entre tabs sin llamar al backend de nuevo
if (typeof BroadcastChannel !== 'undefined') {
  const channel = new BroadcastChannel(CHANNEL_NAME)

  channel.onmessage = ({ data }) => {
    if (data.type === 'REQUEST_SESSION') {
      const { sessionData } = useAuthStore.getState()
      if (sessionData) {
        channel.postMessage({ type: 'SESSION_RESPONSE', sessionData })
      }
    } else if (data.type === 'LOGOUT') {
      const { isAuthenticated } = useAuthStore.getState()
      if (isAuthenticated) {
        clearSessionStorage()
        useAuthStore.getState()._setSentryUser(null)
        useAuthStore.setState({
          isAuthenticated: false,
          isLoading: false,
          user: null,
          tokens: null,
          tokenTimestamp: null,
          sessionData: null,
          error: null,
        })
      }
    }
  }
}

useAuthStore.getState().restoreSession()

export default useAuthStore
